// useFrameworkData — fetches the live DispositionRecords + ClusterRecords from /api/framework and
// falls back to the demo fixtures when the route is not mounted / carries no data yet. This is what
// lets the screen light up with the real 154 SMBC dispositions once the backend is live, while still
// rendering the demo set in dev (inert) with no error surface.

import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../api';
import { useProject } from '../../../contexts/ProjectContext';
import type { DispositionRecord, ClusterRecord, NearMissPair, RoutingStep } from './types';

export interface FrameworkData {
  records: DispositionRecord[];
  clusters: ClusterRecord[];
  // Pairs the overlap algorithm scored just under the clustering threshold. Spec §6 requires these
  // to be visible: "possible overlap, not confirmed" is a consultant's call, not the algorithm's.
  nearMisses: NearMissPair[];
  source: 'empty' | 'live';
  loading: boolean;
  ingesting: boolean;
  // projectId joins in the Logic Decomposer's extraction runs for that project, which is where the
  // formula + output-schema overlap signals come from. Omitted, those two signals stay pending.
  ingest: (file: File, projectId?: number | null) => Promise<{ ok: boolean; count?: number; error?: string }>;
  overrideSurvivor: (clusterId: string, survivorId: string, reason: string) => Promise<{ ok: boolean; error?: string }>;
  // Item 3 — EUC <-> artifact grouping. Each resolves to { ok, error? } and reloads on success,
  // same contract as overrideSurvivor, so callers don't need their own refetch logic.
  // Item 5 ("attach-file-to-inventory-row"): returns the just-updated DispositionRecord too, so a
  // caller that already has a fresh run_id/lineage in hand (LogicDecomposerDashboard's attach
  // flow) doesn't have to read live.records right after the await, which would race the reload()
  // this function already kicked off and see a stale closure instead of the persisted result.
  attachArtifact: (eucId: string, runId: string, projectId?: number | null, originalName?: string | null) => Promise<{ ok: boolean; error?: string; euc?: DispositionRecord }>;
  detachArtifact: (eucId: string, runId: string, projectId?: number | null) => Promise<{ ok: boolean; error?: string }>;
  regroupArtifact: (fromEucId: string, toEucId: string, runId: string, projectId?: number | null) => Promise<{ ok: boolean; error?: string }>;
  // Round 2 UX fix — delete one document/EUC out of the Portfolio list entirely (not just detach
  // one attached run). Removes it from the current project's stored dispositions server-side, then
  // reloads so the list view reflects the deletion — same reload-after-write contract as every
  // other mutator here.
  deleteArtifact: (eucId: string) => Promise<{ ok: boolean; error?: string }>;
  // Item 10 (spec §7.3) — override one EUC's box. Reason required (>=10 chars, enforced server-side).
  overrideDisposition: (eucId: string, disposition: string, reason: string) => Promise<{ ok: boolean; error?: string }>;
  // Re-fetch dispositions + clusters without a full page reload — the "refresh" action on any
  // screen that embeds this hook (item 4's Migration Architect workspace header, in particular),
  // and (item 7) so a per-EUC target-platform save can refresh records with the
  // backend-resolved selected_target_platform without a full page reload.
  reload: () => Promise<void>;
  // Item 11 (spec §10.3, §11) — bulk approve or override a set of EUCs and commit them, routing
  // each to its downstream generation chain in one call (backend: POST /api/framework/commit).
  // A single `decision` applies to every euc_id in the batch, matching the route's own contract —
  // "approve this batch" and "override this batch to box4, here's why" are two distinct actions,
  // not something the frontend stitches together from N single-EUC calls.
  commit: (params: {
    eucIds: string[];
    decision: 'approve' | 'override';
    overrideDisposition?: string;
    reason?: string;
    box3IncludePathC?: boolean;
  }) => Promise<{ ok: boolean; result?: CommitResult; error?: string }>;
}

export interface CommitResult {
  committed: { euc_id: string; disposition: string; routing: RoutingStep[] }[];
  errors: { euc_id: string; error: string }[];
  total: number;
  committed_count: number;
  error_count: number;
}
// No demo fixtures: the screen starts empty until a real inventory is ingested, so a rendered
// number is always the tenant's live data — never a placeholder that could be mistaken for real.
export function useFrameworkData(): FrameworkData {
  const [records, setRecords] = useState<DispositionRecord[]>([]);
  const [clusters, setClusters] = useState<ClusterRecord[]>([]);
  const [nearMisses, setNearMisses] = useState<NearMissPair[]>([]);
  const [source, setSource] = useState<'empty' | 'live'>('empty');
  const [loading, setLoading] = useState(true);
  const [ingesting, setIngesting] = useState(false);

  // The current project scopes every read/write below. Without this, every screen that embeds
  // this hook reads/writes the tenant-wide legacy row (framework_snapshots), which is exactly the
  // "New Project doesn't actually isolate the list" bug — the client-side reset cleared local
  // state, but the very next reload() re-fetched the same mixed, tenant-wide data. project_id
  // omitted -> undefined -> the backend's `projectIdOf(req)` sees nothing and falls back to that
  // same legacy row, so this stays backward compatible for any tenant with no project concept.
  const { currentProject } = useProject();
  const projectId = currentProject?.id != null && Number.isFinite(Number(currentProject.id))
    ? Number(currentProject.id)
    : null;

  const reload = useCallback(async () => {
    try {
      const qs = projectId != null ? `?project_id=${encodeURIComponent(String(projectId))}` : '';
      const [d, c] = await Promise.all([
        apiClient.get(`/api/framework/dispositions${qs}`),
        apiClient.get(`/api/framework/clusters${qs}`),
      ]);
      const items = d?.data?.items;
      if (Array.isArray(items) && items.length) {
        setRecords(items);
        setClusters(Array.isArray(c?.data?.items) ? c.data.items : []);
        setNearMisses(Array.isArray(c?.data?.near_misses) ? c.data.near_misses : []);
        setSource('live');
      } else {
        setRecords([]); setClusters([]); setNearMisses([]); setSource('empty');
      }
    } catch {
      // Route not mounted / no data / auth — stay empty, no error surface.
      setRecords([]); setClusters([]); setNearMisses([]); setSource('empty');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // Upload the Apparity inventory .xlsx → backend builds + caches the 154 records → reload.
  // A caller can still pass an explicit projectId (e.g. a just-created project not yet reflected
  // in context); otherwise this defaults to the current project from context.
  const ingest = useCallback(async (file: File, projectIdOverride?: number | null) => {
    const pid = projectIdOverride !== undefined ? projectIdOverride : projectId;
    setIngesting(true);
    try {
      const form = new FormData();
      form.append('file', file);
      if (pid != null && Number.isFinite(Number(pid))) form.append('project_id', String(pid));
      const res = await apiClient.post('/api/framework/ingest', form);
      await reload();
      return { ok: true, count: res?.data?.count };
    } catch (e: any) {
      return { ok: false, error: e?.response?.data?.error || e?.message || 'ingest failed' };
    } finally {
      setIngesting(false);
    }
  }, [reload, projectId]);

  // A consultant overriding the rule-selected survivor. The reason is required by the spec and is
  // enforced server-side too — an override without a stated reason is not defensible to an auditor.
  const overrideSurvivor = useCallback(async (clusterId: string, survivorId: string, reason: string) => {
    try {
      await apiClient.put(`/api/framework/clusters/${encodeURIComponent(clusterId)}/survivor`, { survivor_id: survivorId, reason, project_id: projectId ?? undefined });
      await reload();
      return { ok: true };
    } catch (e: any) {
      return { ok: false, error: e?.response?.data?.error || e?.message || 'override failed' };
    }
  }, [reload, projectId]);

  // A consultant attaching a decomposed run to an existing EUC — a deliberate action, never a
  // fuzzy auto-match (see eucGrouping.js). Re-scores over the union of everything now attached.
  const attachArtifact = useCallback(async (eucId: string, runId: string, projectIdOverride?: number | null, originalName?: string | null) => {
    const pid = projectIdOverride !== undefined ? projectIdOverride : projectId;
    try {
      const res = await apiClient.post(`/api/framework/dispositions/${encodeURIComponent(eucId)}/artifacts`, {
        run_id: runId, project_id: pid ?? undefined, original_name: originalName ?? undefined,
      });
      await reload();
      return { ok: true, euc: res?.data?.euc };
    } catch (e: any) {
      return { ok: false, error: e?.response?.data?.error || e?.message || 'attach failed' };
    }
  }, [reload, projectId]);

  const detachArtifact = useCallback(async (eucId: string, runId: string, projectIdOverride?: number | null) => {
    const pid = projectIdOverride !== undefined ? projectIdOverride : projectId;
    try {
      const qs = pid != null ? `?project_id=${encodeURIComponent(String(pid))}` : '';
      await apiClient.delete(`/api/framework/dispositions/${encodeURIComponent(eucId)}/artifacts/${encodeURIComponent(runId)}${qs}`);
      await reload();
      return { ok: true };
    } catch (e: any) {
      return { ok: false, error: e?.response?.data?.error || e?.message || 'detach failed' };
    }
  }, [reload, projectId]);

  // Move an artifact from one EUC to another in one call, rather than a detach + a separate
  // attach (which would leave the artifact briefly belonging to neither if the second call fails).
  const regroupArtifact = useCallback(async (fromEucId: string, toEucId: string, runId: string, projectIdOverride?: number | null) => {
    const pid = projectIdOverride !== undefined ? projectIdOverride : projectId;
    try {
      await apiClient.post('/api/framework/dispositions/regroup', {
        from_euc_id: fromEucId, to_euc_id: toEucId, run_id: runId, project_id: pid ?? undefined,
      });
      await reload();
      return { ok: true };
    } catch (e: any) {
      return { ok: false, error: e?.response?.data?.error || e?.message || 'regroup failed' };
    }
  }, [reload, projectId]);

  // Round 2 UX fix — delete one document/EUC from the current project's list, both server-side
  // and (via reload()) in the view. Distinct from detachArtifact: that removes one attached run
  // and only auto-drops the EUC if that empties it; this always removes the whole row.
  const deleteArtifact = useCallback(async (eucId: string) => {
    try {
      const qs = projectId != null ? `?project_id=${encodeURIComponent(String(projectId))}` : '';
      await apiClient.delete(`/api/framework/dispositions/${encodeURIComponent(eucId)}${qs}`);
      await reload();
      return { ok: true };
    } catch (e: any) {
      return { ok: false, error: e?.response?.data?.error || e?.message || 'delete failed' };
    }
  }, [reload, projectId]);

  // Item 10: override one EUC's box. Same required-reason bar as the survivor override above.
  const overrideDisposition = useCallback(async (eucId: string, disposition: string, reason: string) => {
    try {
      await apiClient.put(`/api/framework/dispositions/${encodeURIComponent(eucId)}/override`, { disposition, reason, project_id: projectId ?? undefined });
      await reload();
      return { ok: true };
    } catch (e: any) {
      return { ok: false, error: e?.response?.data?.error || e?.message || 'override failed' };
    }
  }, [reload, projectId]);

  // Item 11: bulk approve/override + commit. Reloads on success so the merged `committed` /
  // `routing` fields (frameworkStore.js's commit_log, merged on read by GET /dispositions) show up
  // immediately — same reload-after-write pattern as ingest/overrideSurvivor/overrideDisposition.
  const commit = useCallback(async (params: {
    eucIds: string[]; decision: 'approve' | 'override'; overrideDisposition?: string; reason?: string; box3IncludePathC?: boolean;
  }) => {
    try {
      const res = await apiClient.post('/api/framework/commit', {
        euc_ids: params.eucIds,
        decision: params.decision,
        override_disposition: params.overrideDisposition,
        reason: params.reason,
        box3_include_path_c: params.box3IncludePathC === true,
        project_id: projectId ?? undefined,
      });
      await reload();
      return { ok: true, result: res?.data as CommitResult };
    } catch (e: any) {
      return { ok: false, error: e?.response?.data?.error || e?.message || 'commit failed' };
    }
  }, [reload, projectId]);

  // Refetches whenever the current project changes (reload's own dep array includes projectId) —
  // switching projects, or creating a new one, now actually swaps the visible artifact list
  // instead of leaving the previous project's fetch results on screen.
  useEffect(() => { reload(); }, [reload]);

  return {
    records, clusters, nearMisses, source, loading, ingesting, ingest, overrideSurvivor,
    attachArtifact, detachArtifact, regroupArtifact, deleteArtifact, overrideDisposition, reload, commit,
  };
}
